import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import ReactRouterPropTypes from 'react-router-prop-types';

import { getAllDoctors } from 'api/doctors';
import { getAllTreatingOrgs } from 'api/treating_orgs';
import { getAllTreatments, updateTreatment } from 'api/treatments';

import { initialFilters, filtersToQueryString } from 'helpers/filtering';
import { setDoctorOptions } from 'helpers/doctor';
import { setTreatingOrgOptions } from 'helpers/treating_org';

import { runUpdater } from 'updaters/treatment';

import Table from 'components/table/table';

import { COLUMNS } from 'config/treatment';

const Treatments = ({ history, onCountUpdate, onMessages }) => {
  const [meta, setMeta] = useState({});
  const [fetching, setFetching] = useState(true);
  const [allTreatments, setAllTreatments] = useState([]);
  const [allDoctors, setAllDoctors] = useState([]);
  const [allTreatingOrgs, setAllTreatingOrgs] = useState([]);
  const [columns, setColumns] = useState(COLUMNS);

  const fetchData = async (page, filters = {}, search) => {
    const filtersString = filtersToQueryString(filters, search);
    const data = await getAllTreatments(page, filtersString);

    setAllTreatments(data.items);
    setMeta(data.meta);
    setFetching(false);
  };

  useEffect(() => {
    (async () => {
      const allDoctors = await getAllDoctors();
      const allTreatingOrgs = await getAllTreatingOrgs();

      const newColumnsWithDocs = setDoctorOptions({
        columns: COLUMNS,
        data: allDoctors,
      });

      const newColumnsWithTreatingOrgs = setTreatingOrgOptions({
        columns: newColumnsWithDocs,
        data: allTreatingOrgs,
      });

      const initFilters = initialFilters(COLUMNS);
      await fetchData(1, initFilters);

      setFetching(false);
      setAllDoctors(allDoctors);
      setAllTreatingOrgs(allTreatingOrgs);
      setColumns(newColumnsWithTreatingOrgs);
    })();
  }, []);

  const onUpdate = async (prop, value, c) => {
    const index = allTreatments.findIndex((t) => t.id === c.id);

    const newTreatments = await runUpdater(
      allTreatments,
      c,
      columns,
      prop,
      value,
      index,
    );

    if (prop === 'doctor') {
      value = allDoctors.find((d) => d.id === value) || null;
    }

    if (prop === 'treatingOrg') {
      value = allTreatingOrgs.find((t) => t.id === value) || null;
    }

    const {
      treatment,
      meta: { messages },
    } = await updateTreatment(c.id, {
      ...c,
      [prop]: value,
    });

    newTreatments[index] = treatment;

    setAllTreatments([...newTreatments]);
    onMessages(messages);
    onCountUpdate();
  };

  const onDateUpdate = async (date, c) => onUpdate('referredAt', date, c);

  return (
    <main className="th-main">
      <section className="th-canvas">
        <header className="th-canvas-header post-header">
          <h2 className="th-canvas-title" data-test-screen-title>
            Treatments
          </h2>
          <section className="view-actions">
            <button
              className="btn-green"
              data-cy="submit"
              onClick={() => history.push('/treatments/add')}
            >
              Add treatment
            </button>
          </section>
        </header>
        <section className="view-container">
          <Table
            columns={columns}
            data={allTreatments}
            meta={meta}
            hasViewBar
            fetching={fetching}
            fetchData={(p, f, s) => fetchData(p, f, s)}
            onSelect={(accessor, value, row) => onUpdate(accessor, value, row)}
            onSingleDatePick={(date, row) => onDateUpdate(date, row)}
          />
        </section>
      </section>
    </main>
  );
};

Treatments.propTypes = {
  history: ReactRouterPropTypes.history.isRequired,
  onCountUpdate: PropTypes.func.isRequired,
  onMessages: PropTypes.func.isRequired,
};

export default Treatments;
